import { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Upload, FileSpreadsheet, FileText, CheckCircle, AlertTriangle, ChevronDown, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ─────────────────────────────────────────────
// UTILITY: Stable placeholder phone derived from username
// e.g. "8city143" → "0038city143" truncated/padded to 11 digits
// Using username ensures same value on re-import (no collision on retry)
// ─────────────────────────────────────────────
const generatePhone = (username) => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) % 1000000000; // 9 digits max
  }
  return `923${String(hash).padStart(9, '0')}`; // 12 digits: 92 + 3XXXXXXXXX
};

// ─────────────────────────────────────────────
// UTILITY: Stable placeholder CNIC derived from username
// Same username always produces same CNIC → safe to re-import
// ─────────────────────────────────────────────
const generateCnic = (username) => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) % 10000000000000; // 13 digits max
  }
  return String(hash).padStart(13, '0');
};

// ─────────────────────────────────────────────
// Ascon (GRT panel) package name → system package name
// ─────────────────────────────────────────────
const ASCON_PKG_MAP = {
  'GRT-3MB-Prepaid+Social Pack': 'Ascon_3',
  'GRT-4MB-Prepaid+Social Pack': 'Ascon_4',
  'GRT-6MB-Prepaid+Social Pack': 'Ascon_6',
  'GRT-8MB-Prepaid+Social Pack': 'Ascon_8',
  'GRT-10MB-Prepaid+Social Pack': 'Ascon_10',
  'GRT-12MB-Prepaid+Social Pack': 'Ascon_12',
};

// Phone: normalises to international format 92XXXXXXXXXX (12 digits, no leading +)
const normalisePhone = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  let digits = String(typeof raw === 'number' ? Math.round(raw) : raw).replace(/\D/g, '');
  if (digits.startsWith('92') && digits.length === 12) return digits;       // already correct
  if (digits.startsWith('0') && digits.length === 11) return '92' + digits.slice(1); // 03xx → 923xx
  if (digits.startsWith('3') && digits.length === 10) return '92' + digits; // 3xx → 923xx
  return digits; // unknown format
};

// CNIC: formats 13-digit number as XXXXX-XXXXXXX-X
const normaliseCnic = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  const digits = String(typeof raw === 'number' ? Math.round(raw) : raw).replace(/\D/g, '');
  if (digits.length === 13) return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits[12]}`;
  return digits;
};

// Date: handles JS Date objects that XLSX returns for date-formatted cells
const formatExcelDate = (val) => {
  if (!val && val !== 0) return '';
  if (val instanceof Date) return val.toISOString().split('T')[0];
  return String(val).trim();
};

// ─────────────────────────────────────────────
// PARSER: Generic CSV row → { name, username, address, package }
// Works for Connect, FalconBroadband, Zong exports.
// Strategy: treat the CSV row as structured columns first,
// then fall back to regex for malformed / PDF text.
// ─────────────────────────────────────────────
const parseStructuredRow = (columns) => {
  // columns is an array of cell values from a well-formed row
  // Common FTTH panel export column order:
  //   [Name, Username, Address, Package, ...]  OR
  //   [#, Username, Name, Package, Address, ...] — Zong style
  // We try both heuristics.

  if (!columns || columns.length < 2) return null;

  const cols = columns.map(c => String(c ?? '').trim());

  // Detect if col[0] is a numeric index (Zong/Connect style: #, Username, Name, ...)
  const isIndexed = /^\d+$/.test(cols[0]);

  let name, username, address, pkg;

  if (isIndexed) {
    // Format: index | username | name | package | address
    [, username, name, pkg, address] = cols;
  } else {
    // Format: name | username | address | package
    [name, username, address, pkg] = cols;
  }

  if (!username || username.length < 3) return null;
  if (!pkg) return null;

  return {
    name: name || username,
    username: username.trim(),
    address: address || '',
    package: pkg.trim().toUpperCase(),
  };
};

// ─────────────────────────────────────────────
// PARSER: Unstructured line → customer (PDF / broken CSV fallback)
// ─────────────────────────────────────────────
const extractCustomerFromLine = (line) => {
  // Match any package-like code: letters, digits, dashes
  // Handles: MK-GUJ-10MB, MK-GUJ-25MB, MK-GUJ-50MB, FLK-20MB, ZNG-30M, etc.
  const pkgMatch = line.match(/[A-Z]{2,5}-[A-Z]{0,5}-?\d+\s?M[bB]?[sS]?/i);
  if (!pkgMatch) return null;

  // Match username: alphanumeric block of 4–30 chars (no spaces), may include _ -
  // Excludes the package token and very short strings
  const tokens = line.split(/\s+|,|"/).map(t => t.trim()).filter(Boolean);
  const pkgToken = pkgMatch[0];

  // Heuristic: username is a token that:
  // - is 4–30 chars
  // - contains at least one digit AND one letter
  // - is not the package itself
  let username = '';
  for (const token of tokens) {
    if (
      token !== pkgToken &&
      token.length >= 4 &&
      token.length <= 30 &&
      /[a-zA-Z]/.test(token) &&
      /\d/.test(token)
    ) {
      username = token;
      break;
    }
  }

  if (!username) return null;

  // Name: everything before the username token, stripped of bullets / quotes
  const userIdx = line.indexOf(username);
  const namePart = line
    .slice(0, userIdx)
    .replace(/[●•"\d,]/g, '')
    .trim();

  // Address: between username and package
  const pkgIdx = line.indexOf(pkgToken);
  const address = line
    .slice(userIdx + username.length, pkgIdx)
    .replace(/"/g, '')
    .trim();

  return {
    name: namePart || username,
    username,
    address,
    package: pkgToken.toUpperCase().replace(/\s/g, ''),
  };
};

// ─────────────────────────────────────────────
// PARSER: CSV text → customer array
// ─────────────────────────────────────────────
const parseCSVText = (text) => {
  const lines = text.split('\n').filter(l => l.trim());
  const results = [];

  // Try to detect header row
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes('username') || firstLine.includes('name') || firstLine.includes('package');
  const dataLines = hasHeader ? lines.slice(1) : lines;

  for (const line of dataLines) {
    // Try structured parse first
    const columns = line.split(',').map(c => c.replace(/"/g, '').trim());
    const structured = parseStructuredRow(columns);
    if (structured) {
      results.push(structured);
      continue;
    }
    // Fallback: regex
    const fallback = extractCustomerFromLine(line);
    if (fallback) results.push(fallback);
  }
  return results;
};

// ─────────────────────────────────────────────
// PARSER: Excel workbook → customer array
// Handles multiple panel export formats:
//   - Falcon / Connect / Zong: Name | Username | Address | Package | ...
//   - Ascon (GRT panel): ID | (empty) | Profile | Expiration | Packages | Identity | Phone | Address
//   - Plain indexed: # | Username | Name | Package | Address
// ─────────────────────────────────────────────
const parseExcelData = (workbook) => {
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '', raw: true });

  if (rows.length === 0) return [];

  // Find header row: first row with "username", "profile", or "name"
  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 5); i++) {
    const hasIdent = rows[i].some(c => /^(username|profile|name)$/i.test(String(c ?? '').trim()));
    if (hasIdent) { headerRowIdx = i; break; }
  }

  const dataRows = headerRowIdx >= 0 ? rows.slice(headerRowIdx + 1) : rows;
  const hdr = headerRowIdx >= 0
    ? rows[headerRowIdx].map(c => String(c ?? '').trim().toLowerCase())
    : [];

  // Flexible column finder: exact first, then substring
  const findCol = (...names) => {
    for (const n of names) {
      const idx = hdr.findIndex(c => c === n.toLowerCase());
      if (idx >= 0) return idx;
    }
    for (const n of names) {
      const nl = n.toLowerCase();
      const idx = hdr.findIndex(c => c.includes(nl) || nl.includes(c));
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const profileCol = findCol('profile');
  const nameCol    = profileCol >= 0 ? -1 : (findCol('name') >= 0 ? findCol('name') : 0);
  const userCol    = profileCol >= 0 ? -1 : (findCol('username') >= 0 ? findCol('username') : 1);
  const addrCol    = findCol('address');
  const pkgCol     = findCol('packages', 'package', 'plan');
  const phoneCol   = findCol('phone', 'mobile', 'contact');
  const cnicCol    = findCol('identity', 'cnic', 'id number');
  const expiryCol  = findCol('expiration', 'expiry', 'expiry date', 'valid until');

  const results = [];
  for (const row of dataRows) {
    if (row.every(c => c === '' || c === null || c === undefined)) continue;

    let name = '', username = '';

    if (profileCol >= 0 && row[profileCol]) {
      // Ascon format: "Sher zaman     1city021" — last token is username
      const parts = String(row[profileCol]).trim().split(/\s+/);
      username = parts[parts.length - 1] || '';
      name = parts.slice(0, -1).join(' ');
    } else {
      const rawName = nameCol >= 0 ? String(row[nameCol] ?? '') : '';
      name = rawName.replace(/[●•]/g, '').trim();
      username = userCol >= 0 ? String(row[userCol] ?? '').trim() : '';
    }

    if (!username || username.length < 3) continue;
    if (/^(username|profile|name)$/i.test(username)) continue;

    const rawPkg = pkgCol >= 0 ? String(row[pkgCol] ?? '').trim() : '';
    if (!rawPkg || /^(package|packages|plan)$/i.test(rawPkg)) continue;

    // Map Ascon GRT names → system names; fall back to uppercase for others
    const mappedPkg = ASCON_PKG_MAP[rawPkg] || rawPkg.toUpperCase();

    results.push({
      name: name || username,
      username: username.trim(),
      address:    addrCol >= 0  ? String(row[addrCol] ?? '').trim() : '',
      package:    mappedPkg,
      phone:      phoneCol >= 0  ? normalisePhone(row[phoneCol])  : '',
      cnic:       cnicCol >= 0   ? normaliseCnic(row[cnicCol])    : '',
      expiry_date: expiryCol >= 0 ? formatExcelDate(row[expiryCol]) : '',
    });
  }
  return results;
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const ImportCustomersModal = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [preview, setPreview] = useState([]);
  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null); // null = not yet run
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [parseWarning, setParseWarning] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, areaRes] = await Promise.all([
          api.get('/packages/'),
          api.get('/areas/'),
        ]);
        setPackages(pkgRes.data.results ?? pkgRes.data);
        const fetchedAreas = areaRes.data.results ?? areaRes.data;
        setAreas(fetchedAreas);
        if (fetchedAreas.length > 0) setSelectedArea(fetchedAreas[0].id);
      } catch (err) {
        console.error('Failed to load packages/areas:', err);
      }
    };
    fetchData();
  }, []);

  // ── File selection & parsing ──────────────────
  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const name = selected.name.toLowerCase();
    let type = null;
    if (name.endsWith('.csv')) type = 'csv';
    else if (name.endsWith('.xlsx') || name.endsWith('.xls')) type = 'excel';
    else if (name.endsWith('.pdf')) type = 'pdf';
    else {
      alert('Please upload a .csv, .xlsx, or .pdf file');
      return;
    }

    setFile(selected);
    setFileType(type);
    setParsedData([]);
    setPreview([]);
    setStats(null);
    setErrors([]);
    setParseWarning('');
    setParsing(true);

    try {
      let results = [];

      if (type === 'csv') {
        const text = await selected.text();
        results = parseCSVText(text);
      } else if (type === 'excel') {
        const buf = await selected.arrayBuffer();
        const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
        results = parseExcelData(wb);
      } else if (type === 'pdf') {
        const buf = await selected.arrayBuffer();
        const pdf = await pdfjsLib.getDocument(buf).promise;
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          fullText += content.items.map(item => item.str).join(' ') + '\n';
        }
        for (const line of fullText.split('\n').filter(l => l.trim())) {
          const c = extractCustomerFromLine(line);
          if (c) results.push(c);
        }
      }

      if (results.length === 0) {
        setParseWarning(
          'No customers were detected. The file format may not match expected columns (Name, Username, Address, Package). Check the file and try again.'
        );
      }

      setParsedData(results);
      setPreview(results.slice(0, 8));
    } catch (err) {
      console.error('Parse error:', err);
      setParseWarning(`Parse failed: ${err.message}`);
    } finally {
      setParsing(false);
    }
  };

  // ── Match package name → DB id ────────────────
  const matchPackage = (pkgName) => {
    const normalized = pkgName.trim().toUpperCase();
    return packages.find(
      p =>
        p.name.trim().toUpperCase() === normalized ||
        p.name.trim().toUpperCase().replace(/\s/g, '') === normalized.replace(/\s/g, '')
    );
  };

  // ── Import ────────────────────────────────────
  const handleImport = async () => {
    if (!parsedData.length) return;
    setLoading(true);
    setProgress(0);
    setStats(null);
    setErrors([]);

    let successCount = 0;
    let failCount = 0;
    const errorLog = [];

    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];
      const pkgObj = matchPackage(row.package);

      if (!pkgObj) {
        failCount++;
        errorLog.push({
          username: row.username,
          reason: `Package "${row.package}" not found in system. Available: ${packages.map(p => p.name).join(', ')}`,
        });
        setProgress(Math.round(((i + 1) / parsedData.length) * 100));
        continue;
      }

      try {
        const payload = {
          username: row.username,
          name: row.name || row.username,
          address: row.address || '',
          // Use real values from file; fall back to stable username-derived placeholders
          phone: row.phone || generatePhone(row.username),
          cnic: row.cnic || generateCnic(row.username),
          package: pkgObj.id,
          area: selectedArea || null,
          status: 'active',
        };
        if (row.expiry_date) payload.expiry_date = row.expiry_date;
        await api.post('/customers/', payload);
        successCount++;
      } catch (err) {
        failCount++;
        const detail = err.response?.data
          ? Object.entries(err.response.data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : err.message;
        errorLog.push({ username: row.username, reason: detail });
      }

      setProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }

    setStats({ success: successCount, failed: failCount });
    setErrors(errorLog);
    setLoading(false);

    if (failCount === 0) {
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    }
  };

  const reset = () => {
    setFile(null);
    setFileType(null);
    setParsedData([]);
    setPreview([]);
    setStats(null);
    setErrors([]);
    setParseWarning('');
  };

  // ── Render ────────────────────────────────────
  const unmatchedPackages = [...new Set(parsedData.map(r => r.package))].filter(
    p => !matchPackage(p)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {fileType === 'pdf'
              ? <FileText size={20} className="text-red-500" />
              : <FileSpreadsheet size={20} className="text-emerald-600" />}
            Bulk Import Customers
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* ── Drop zone ── */}
          {!file && (
            <div
              onClick={() => document.getElementById('importFileInput').click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
            >
              <Upload className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium">Click to upload or drag & drop</p>
              <p className="text-xs text-gray-400 mt-1">
                Supports CSV, Excel (.xlsx), PDF — Connect, FalconBroadband, Zong, Ascon exports
              </p>
              <input
                id="importFileInput"
                type="file"
                accept=".csv,.xlsx,.xls,.pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {/* ── File info bar ── */}
          {file && (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                {fileType === 'pdf'
                  ? <FileText size={16} className="text-red-500" />
                  : <FileSpreadsheet size={16} className="text-emerald-600" />}
                <span className="font-medium">{file.name}</span>
                {!parsing && parsedData.length > 0 && (
                  <span className="text-gray-400">— {parsedData.length} rows detected</span>
                )}
              </div>
              {!loading && (
                <button onClick={reset} className="text-xs text-red-500 hover:underline">
                  Remove
                </button>
              )}
            </div>
          )}

          {/* ── Parsing spinner ── */}
          {parsing && (
            <p className="text-center text-sm text-gray-500 animate-pulse">Parsing file…</p>
          )}

          {/* ── Parse warning ── */}
          {parseWarning && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <p>{parseWarning}</p>
            </div>
          )}

          {/* ── Unmatched packages warning ── */}
          {unmatchedPackages.length > 0 && !loading && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Package name mismatch detected</p>
                <p className="mt-0.5">
                  These packages in the file have no match in the system:{' '}
                  <strong>{unmatchedPackages.join(', ')}</strong>
                </p>
                <p className="mt-0.5 text-amber-700">
                  System packages: {packages.map(p => p.name).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* ── Area selector ── */}
          {parsedData.length > 0 && !loading && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Assign to Area
              </label>
              <div className="relative flex-1">
                <select
                  value={selectedArea}
                  onChange={e => setSelectedArea(e.target.value)}
                  className="w-full appearance-none border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— No area —</option>
                  {areas.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* ── Preview table ── */}
          {preview.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                Preview (first {preview.length} rows)
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 border-b border-gray-200">Name</th>
                      <th className="px-3 py-2 border-b border-gray-200">Username</th>
                      <th className="px-3 py-2 border-b border-gray-200">Package</th>
                      <th className="px-3 py-2 border-b border-gray-200">Match?</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.map((row, idx) => {
                      const matched = !!matchPackage(row.package);
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700 max-w-[120px] truncate" title={row.name}>
                            {row.name}
                          </td>
                          <td className="px-3 py-2 text-gray-700 font-mono">{row.username}</td>
                          <td className="px-3 py-2 text-gray-600">{row.package}</td>
                          <td className="px-3 py-2">
                            {matched ? (
                              <span className="text-emerald-600 font-medium">✓</span>
                            ) : (
                              <span className="text-red-500 font-medium">✗</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Progress bar ── */}
          {loading && (
            <div className="space-y-1.5">
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-xs text-gray-500">Importing… {progress}%</p>
            </div>
          )}

          {/* ── Result banner ── */}
          {stats && (
            <div className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${
              stats.failed === 0
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border border-amber-200 text-amber-800'
            }`}>
              <CheckCircle size={16} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">
                  {stats.success} imported successfully
                  {stats.failed > 0 && `, ${stats.failed} failed`}
                </p>
                {errors.length > 0 && (
                  <button
                    onClick={() => setShowErrors(v => !v)}
                    className="mt-1 flex items-center gap-1 text-xs underline underline-offset-2"
                  >
                    <Eye size={12} />
                    {showErrors ? 'Hide' : 'Show'} error details
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Error details ── */}
          {showErrors && errors.length > 0 && (
            <div className="border border-red-100 rounded-lg max-h-40 overflow-y-auto text-xs">
              <table className="w-full text-left">
                <thead className="bg-red-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-red-700">Username</th>
                    <th className="px-3 py-2 text-red-700">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-50">
                  {errors.map((e, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-1.5 font-mono text-gray-700">{e.username}</td>
                      <td className="px-3 py-1.5 text-red-600">{e.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Action buttons ── */}
          {!loading && parsedData.length > 0 && !stats && (
            <button
              onClick={handleImport}
              disabled={parsing}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-2.5 rounded-lg font-semibold text-sm transition-colors shadow-sm"
            >
              Import {parsedData.length} Customers
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportCustomersModal;
